import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  User,
  Mail,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Loader2,
  Search,
  MailOpen,
  History,
  Eye,
  Clock,
  Send,
  Tag,
  FileText,
  Link as LinkIcon,
  Upload,
  Download,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import Papa from "papaparse";
import EmailAnalyticsDashboard from "./EmailAnalyticsDashboard";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../../firebase.init";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";


const UserEmailTemplateManager = () => {
  const [user] = useAuthState(auth);
  const [templates, setTemplates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageForm, setMessageForm] = useState({
    toEmail: "",
    subject: "",
    messageBody: "",
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [formData, setFormData] = useState({
    userName: "",
    UserEmail: "",
    category: "forSubscriber",
    customFields: [],
  });
  const [categories, setCategories] = useState([
    "forSubscriber",
    "forRegister",
  ]);
  const [isAddingNewCategory, setIsAddingNewCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importTab, setImportTab] = useState("csv"); // 'csv' or 'sheets'
  const [googleSheetLink, setGoogleSheetLink] = useState("");

  const itemsPerPage = 10;

  useEffect(() => {
    if (user?.email) {
      fetchTemplates();
      fetchEmailTemplates();
    }
  }, [user]);

  const fetchTemplates = async () => {
    if (!user?.email) return;
    try {
      const res = await axios.get(`https://demo-email-marketing-1-f6e91377e127.herokuapp.com/user-email-templates?ownerEmail=${user.email}`);
      setTemplates(res.data);

      // Extract unique categories
      const existingCategories = [
        ...new Set(res.data.map((u) => u.category).filter(Boolean)),
      ];
      setCategories((prev) => [...new Set([...prev, ...existingCategories])]);
    } catch (err) {
      toast.error("Error fetching data");
    }
  };

  const fetchEmailTemplates = async () => {
    if (!user?.email) return;
    try {
      const response = await fetch(`https://demo-email-marketing-1-f6e91377e127.herokuapp.com/user-emails?ownerEmail=${user.email}`);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setEmailTemplates(data);
    } catch (err) {
      toast.error(`Failed to fetch email templates: ${err.message}`);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingId) {
        await axios.put(
          `https://demo-email-marketing-1-f6e91377e127.herokuapp.com/edit-user-email-template/${editingId}`,
          { ...formData, ownerEmail: user?.email },
        );
        toast.success("User updated ✅");
      } else {
        await axios.post("https://demo-email-marketing-1-f6e91377e127.herokuapp.com/add-user-email-template", {
          ...formData,
          enabled: true,
          ownerEmail: user?.email,
        });
        toast.success("User added ✅");
      }

      setFormData({
        userName: "",
        UserEmail: "",
        category: "forSubscriber",
        customFields: [],
      });
      setEditingId(null);
      setShowUserModal(false);
      setIsAddingNewCategory(false);
      setNewCategory("");
      fetchTemplates();
    } catch (err) {
      toast.error("Something went wrong ❌");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setFormData({
      userName: user.userName,
      UserEmail: user.UserEmail,
      category: user.category || "forSubscriber",
      customFields: user.customFields || [],
    });
    setEditingId(user._id);
    setShowUserModal(true);
  };

  const cancelEdit = () => {
    setFormData({
      userName: "",
      UserEmail: "",
      category: "forSubscriber",
      customFields: [],
    });
    setEditingId(null);
    setShowUserModal(false);
  };

  const handleAddField = () => {
    setFormData((prev) => ({
      ...prev,
      customFields: [
        ...prev.customFields,
        { label: "", value: "", shortcode: "" },
      ],
    }));
  };

  const handleFieldChange = (index, field, value) => {
    const updatedFields = [...formData.customFields];
    updatedFields[index][field] = value;

    if (field === "label") {
      updatedFields[index].shortcode = value
        .toLowerCase()
        .replace(/ /g, "_")
        .replace(/[^\w]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      customFields: updatedFields,
    }));
  };

  const handleRemoveField = (index) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
  };

  const handleTemplateChange = (e) => {
    const selectedTemplate = emailTemplates.find(
      (template) => template._id === e.target.value,
    );
    if (selectedTemplate) {
      const user = templates.find((t) => t.UserEmail === messageForm.toEmail);
      const userName = user?.userName || "User";

      let subjectWithUserName = replaceShortcodesClient(
        selectedTemplate.subject,
        userName,
      );
      let bodyWithUserName = replaceShortcodesClient(
        selectedTemplate.body || "",
        userName,
      );

      setMessageForm({
        ...messageForm,
        subject: subjectWithUserName,
        messageBody: bodyWithUserName,
      });
    } else {
      setMessageForm({
        ...messageForm,
        subject: "",
        messageBody: "",
      });
    }
  };

  // Helper to replace shortcodes on the client side for preview
  const replaceShortcodesClient = (content, userName) => {
    if (!content) return "";
    let result = content;
    result = result.replace(/{{user_name}}/g, userName);
    result = result.replace(/{{first_name}}/g, userName.split(" ")[0]);
    result = result.replace(/{user\?\.userName}/g, userName);
    return result;
  };

  const handleSendMessage = async () => {
    setSendingMessage(true);
    try {
      const response = await axios.post(
        "https://demo-email-marketing-1-f6e91377e127.herokuapp.com/send-message",
        messageForm,
      );
      if (response.status === 200) {
        toast.success("Message sent successfully.");
        setShowMessageModal(false);
        setMessageForm({ toEmail: "", subject: "", messageBody: "" });
      } else {
        toast.error("Failed to send message.");
      }
    } catch (err) {
      toast.error("Error sending message.");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBulkImport = async (data) => {
    setImportLoading(true);
    try {
      const response = await axios.post(
        "https://demo-email-marketing-1-f6e91377e127.herokuapp.com/bulk-import-contacts",
        { contacts: data, ownerEmail: user?.email },
      );
      if (response.data.success) {
        toast.success(response.data.message);
        setShowImportModal(false);
        fetchTemplates();
      } else {
        toast.error("Import failed");
      }
    } catch (err) {
      toast.error("Error during import");
      console.error(err);
    } finally {
      setImportLoading(false);
    }
  };

  const processImportData = (results) => {
    const data = results.data;
    const mappedContacts = data
      .filter((row) => {
        const keys = Object.keys(row);
        const emailKey = keys.find(
          (k) =>
            k.toLowerCase().trim() === "email" ||
            k.toLowerCase().trim() === "useremail",
        );
        return emailKey && row[emailKey];
      })
      .map((row) => {
        const keys = Object.keys(row);
        const getValue = (targetKey) => {
          const key = keys.find(
            (k) => k.toLowerCase().trim() === targetKey.toLowerCase(),
          );
          return key ? row[key] : null;
        };

        const email = getValue("email") || getValue("useremail");
        const name =
          getValue("name") ||
          getValue("username") ||
          (email ? email.split("@")[0] : "User");
        const category = getValue("category") || "forSubscriber";

        const standardKeys = [
          "email",
          "useremail",
          "name",
          "username",
          "category",
        ];
        const customFields = keys
          .filter((key) => !standardKeys.includes(key.toLowerCase().trim()))
          .map((key) => ({
            label: key,
            value: row[key],
            shortcode: key
              .toLowerCase()
              .trim()
              .replace(/ /g, "_")
              .replace(/[^\w]/g, ""),
          }));

        return {
          userName: name,
          UserEmail: email,
          category: category,
          customFields: customFields,
          enabled: true,
        };
      });

    if (mappedContacts.length === 0) {
      toast.error("No valid contacts found");
      setImportLoading(false);
      return;
    }

    handleBulkImport(mappedContacts);
  };

  const downloadSampleCSV = () => {
    const headers = ["UserEmail", "userName", "category", "Phone", "Location"];
    const rows = [
      [
        "john@example.com",
        "John Doe",
        "forSubscriber",
        "+123456789",
        "New York",
      ],
      ["jane@example.com", "Jane Smith", "forRegister", "+987654321", "London"],
    ];

    let csvContent = headers.join(",") + "\n";
    rows.forEach((row) => {
      csvContent += row.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "contacts_sample.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: processImportData,
        error: (err) => {
          toast.error("Error parsing CSV");
          console.error(err);
        },
      });
    }
  };

  const handleGoogleSheetsImport = async () => {
    if (!googleSheetLink) {
      toast.error("Please enter a Google Sheets link");
      return;
    }

    const match = googleSheetLink.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (!match) {
      toast.error("Invalid Google Sheets link");
      return;
    }

    const sheetId = match[1];
    const exportUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`;

    setImportLoading(true);
    try {
      const response = await axios.get(exportUrl);
      Papa.parse(response.data, {
        header: true,
        skipEmptyLines: true,
        complete: processImportData,
        error: (err) => {
          toast.error("Error parsing data");
          setImportLoading(false);
        },
      });
    } catch (err) {
      toast.error("Failed to fetch sheet. Ensure it is public.");
      setImportLoading(false);
    }
  };

  const totalPages = Math.ceil(templates.length / itemsPerPage);

  const getPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }
    return pages;
  };

  const formatDate = (dateStr) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateStr).toLocaleString("en-US", options);
  };

  const filteredTemplates = templates.filter((t) =>
    `${t.userName} ${t.UserEmail}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const paginatedTemplates = filteredTemplates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const [showLogModal, setShowLogModal] = useState(false);
  const [selectedUserLogs, setSelectedUserLogs] = useState([]);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  const [logPage, setLogPage] = useState(1);
  const logsPerPage = 5;
  const totalLogPages = Math.ceil(selectedUserLogs.length / logsPerPage);

  const paginatedLogs = selectedUserLogs.slice(
    (logPage - 1) * logsPerPage,
    logPage * logsPerPage,
  );

  const handleLogPageChange = (direction) => {
    if (direction === "prev" && logPage > 1) setLogPage(logPage - 1);
    if (direction === "next" && logPage < totalLogPages)
      setLogPage(logPage + 1);
  };

  const handleToggleUser = async (user) => {
    try {
      const updatedUser = {
        ...user,
        enabled: !user.enabled,
      };

      await axios.put(
        `https://demo-email-marketing-1-f6e91377e127.herokuapp.com/edit-user-email-template/${user._id}`,
        updatedUser,
      );

      toast.success(
        `User ${updatedUser.enabled ? "enabled" : "disabled"} successfully`,
      );
      fetchTemplates(); // Refresh list
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`https://demo-email-marketing-1-f6e91377e127.herokuapp.com/user-email-template/${id}?ownerEmail=${user?.email}`);
        toast.success("Deleted successfully 🗑️");
        fetchTemplates();
      } catch (err) {
        toast.error("Failed to delete ❌");
      }
    }
  };

  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(paginatedTemplates.map((user) => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} selected users?`)) {
      try {
        await axios.post('https://demo-email-marketing-1-f6e91377e127.herokuapp.com/bulk-delete-user-email-templates', { ids: selectedUsers, ownerEmail: user?.email });
        toast.success('Selected users deleted successfully 🗑️');
        setSelectedUsers([]);
        fetchTemplates();
      } catch (err) {
        toast.error('Failed to delete selected users ❌');
      }
    }
  };

  return (
    <div
      className="container-fluid my-5"
      id="premium-dark-manager"
      style={{ maxWidth: "1400px" }}
    >
      <Link 
        to="/" 
        className="btn btn-outline-primary rounded-pill px-3 py-1 d-inline-flex align-items-center gap-2 mb-4 text-white"
        style={{ fontSize: '0.85rem' }}
      >
        <ArrowLeft size={16} />
        Back to Dashboard
      </Link>
      {/* Header with Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0 d-flex align-items-center text-white">
          <Mail className="me-2" />
          Manually User Marketing
        </h4>
        <div className="d-flex align-items-center">
          {selectedUsers.length > 0 && (
            <button
              className="btn btn-danger d-flex align-items-center me-2"
              onClick={handleBulkDelete}
            >
              <Trash2 size={18} className="me-2" />
              Delete Selected ({selectedUsers.length})
            </button>
          )}
          <button
            className="btn btn-primary d-flex align-items-center"
            onClick={() => {
              setEditingId(null);
              setFormData({
                userName: "",
                UserEmail: "",
                category: "forSubscriber",
                customFields: [],
              });
              setShowUserModal(true);
            }}
          >
            <Plus size={18} className="me-2" />
            Add User
          </button>
          <button
            className="btn btn-outline-primary d-flex align-items-center ms-2 text-white"
            onClick={() => setShowImportModal(true)}
          >
            <Upload size={18} className="me-2" />
            Import Contacts
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="d-flex justify-content-end my-3">
        <div className="input-group w-100">
          <span className="input-group-text bg-light">
            <Search size={16} />
          </span>
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* List */}
      <div className="card shadow border-0">
        <div className="card-header bg-light">
          <h6 className="mb-0">Saved Users</h6>
        </div>
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      paginatedTemplates.length > 0 &&
                      selectedUsers.length === paginatedTemplates.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>
                <th>#</th>
                <th>User Name</th>
                <th>Email</th>
                <th>Category</th>
                <th>Message</th>
                <th>Message History</th>
                <th>Status</th>
                <th>Added At</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTemplates.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-3 text-muted">
                    No User Found
                  </td>
                </tr>
              ) : (
                paginatedTemplates.map((user, index) => (
                  <tr key={user._id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => handleSelectUser(user._id)}
                      />
                    </td>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>{user.userName}</td>
                    <td>{user.UserEmail}</td>
                    <td>
                      <span
                        className={`badge ${user.category === "forSubscriber" ? "bg-info" : user.category === "forRegister" ? "bg-success" : "bg-secondary"}`}
                      >
                        {user.category === "forSubscriber"
                          ? "For Subscriber"
                          : user.category === "forRegister"
                            ? "For Register"
                            : user.category || "N/A"}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() =>
                          setMessageForm({
                            toEmail: user.UserEmail,
                            subject: "",
                            messageBody: "",
                          }) || setShowMessageModal(true)
                        }
                      >
                        <Mail size={16} />
                      </button>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => {
                          setSelectedUserLogs(user.messageLogs || []);
                          setLogPage(1);
                          setShowLogModal(true);
                        }}
                      >
                        <History size={16} />
                      </button>
                    </td>

                    {/* ✅ ENABLE/DISABLE TOGGLE */}
                    <td>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={user.enabled ?? true} // fallback to true if undefined
                          onChange={() => handleToggleUser(user)}
                        />
                      </div>
                    </td>

                    <td>{user.createdAt ? formatDate(user.createdAt) : "-"}</td>

                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-warning me-2"
                        onClick={() => handleEdit(user)}
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(user._id)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="card-footer bg-white">
          <nav>
            <ul className="pagination justify-content-center mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>
              </li>

              {getPagination().map((page, i) =>
                page === "..." ? (
                  <li key={i} className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                ) : (
                  <li
                    key={i}
                    className={`page-item ${
                      page === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </li>
                ),
              )}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Send Message</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMessageModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Email Template</label>
                  <select
                    className="form-select"
                    onChange={handleTemplateChange}
                    defaultValue=""
                  >
                    <option value="">Select a template</option>
                    {emailTemplates
                      .filter((template) => {
                        // Find the recipient user to match category
                        const recipientUser = templates.find(
                          (t) => t.UserEmail === messageForm.toEmail,
                        );
                        if (!recipientUser || !recipientUser.category)
                          return true;
                        return template.category === recipientUser.category;
                      })
                      .map((template) => (
                        <option key={template._id} value={template._id}>
                          {template.templateName}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">To Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={messageForm.toEmail}
                    onChange={(e) =>
                      setMessageForm({
                        ...messageForm,
                        toEmail: e.target.value,
                      })
                    }
                    readOnly
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageForm.subject}
                    onChange={(e) =>
                      setMessageForm({
                        ...messageForm,
                        subject: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    Message Body (HTML allowed)
                  </label>
                  <textarea
                    className="form-control"
                    rows="6"
                    placeholder="<h2>Hello</h2><p>This is <strong>bold</strong> text</p>"
                    value={messageForm.messageBody}
                    onChange={(e) =>
                      setMessageForm({
                        ...messageForm,
                        messageBody: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowMessageModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSendMessage}
                  disabled={sendingMessage}
                >
                  {sendingMessage ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showLogModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <h5 className="modal-title d-flex align-items-center">
                  <History size={20} className="me-2" />
                  Message Log History
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowLogModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {selectedUserLogs.length === 0 ? (
                  <div className="text-center py-5">
                    <MailOpen size={48} className="text-muted mb-3" />
                    <p className="text-muted fs-5">No message logs found.</p>
                  </div>
                ) : (
                  <>
                    <table className="table table-hover align-middle">
                      <thead style={{ background: "#f8f9fa" }}>
                        <tr>
                          <th style={{ width: "40px" }}>#</th>
                          <th style={{ width: "60px" }}>Day</th>
                          <th>Sent Date</th>
                          <th>Template Name</th>
                          <th>Type</th>
                          <th style={{ width: "60px" }}>View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedLogs.map((log, idx) => (
                          <tr
                            key={idx}
                            style={{ cursor: "pointer" }}
                            onClick={() => setSelectedLogDetail(log)}
                            className="log-row-hover"
                          >
                            <td>{(logPage - 1) * logsPerPage + idx + 1}</td>
                            <td>
                              <span className="badge bg-primary rounded-pill">
                                {log.day}
                              </span>
                            </td>
                            <td>{formatDate(log.date)}</td>
                            <td className="fw-semibold">{log.templateName}</td>
                            <td>
                              <span
                                className={`badge ${log.sendType === "manual" ? "bg-warning text-dark" : "bg-info"}`}
                              >
                                {log.sendType === "manual"
                                  ? "Manual"
                                  : log.sendType === "scheduled"
                                    ? "Scheduled"
                                    : "Auto"}
                              </span>
                            </td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLogDetail(log);
                                }}
                              >
                                <Eye size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {totalLogPages > 1 && (
                      <nav className="d-flex justify-content-center">
                        <ul className="pagination">
                          <li
                            className={`page-item ${
                              logPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handleLogPageChange("prev")}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(totalLogPages)].map((_, i) => (
                            <li
                              key={i}
                              className={`page-item ${
                                logPage === i + 1 ? "active" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => setLogPage(i + 1)}
                              >
                                {i + 1}
                              </button>
                            </li>
                          ))}
                          <li
                            className={`page-item ${
                              logPage === totalLogPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handleLogPageChange("next")}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowLogModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Detail Modal */}
      {selectedLogDetail && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1060 }}
          onClick={() => setSelectedLogDetail(null)}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="modal-content"
              style={{ borderRadius: "16px", overflow: "hidden" }}
            >
              {/* Gradient Header */}
              <div
                className="modal-header border-0"
                style={{
                  background:
                    "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
                  color: "white",
                  padding: "24px 30px",
                }}
              >
                <div>
                  <h5
                    className="modal-title mb-1 d-flex align-items-center text-white"
                    style={{ fontSize: "1.25rem" }}
                  >
                    <MailOpen size={22} className="me-2" />
                    Email Details
                  </h5>
                  <p
                    className="mb-0"
                    style={{ opacity: 0.7, fontSize: "0.85rem" }}
                  >
                    {selectedLogDetail.templateName} •{" "}
                    {formatDate(selectedLogDetail.date)}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setSelectedLogDetail(null)}
                ></button>
              </div>

              <div className="modal-body p-0">
                {/* Info Cards Row */}
                <div className="p-4" style={{ background: "#f8f9fc" }}>
                  <div className="row g-3">
                    {/* Recipient Card */}
                    <div className="col-md-4">
                      <div className="d-flex align-items-center p-3 bg-white rounded-3 shadow-sm h-100">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "44px",
                            height: "44px",
                            background:
                              "linear-gradient(135deg, #667eea, #764ba2)",
                            flexShrink: 0,
                          }}
                        >
                          <Mail size={20} color="white" />
                        </div>
                        <div style={{ overflow: "hidden" }}>
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Recipient
                          </div>
                          <div
                            className="fw-semibold text-truncate"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {selectedLogDetail.recipientName || "—"}
                          </div>
                          <div
                            className="text-muted text-truncate"
                            style={{ fontSize: "0.8rem" }}
                          >
                            {selectedLogDetail.recipientEmail || "—"}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Send Info Card */}
                    <div className="col-md-4">
                      <div className="d-flex align-items-center p-3 bg-white rounded-3 shadow-sm h-100">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "44px",
                            height: "44px",
                            background:
                              "linear-gradient(135deg, #f093fb, #f5576c)",
                            flexShrink: 0,
                          }}
                        >
                          <Send size={20} color="white" />
                        </div>
                        <div>
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Send Info
                          </div>
                          <div
                            className="fw-semibold"
                            style={{ fontSize: "0.9rem" }}
                          >
                            Day {selectedLogDetail.day}
                          </div>
                          <div>
                            <span
                              className={`badge ${selectedLogDetail.sendType === "manual" ? "bg-warning text-dark" : "bg-info"}`}
                              style={{ fontSize: "0.7rem" }}
                            >
                              {selectedLogDetail.sendType === "manual"
                                ? "✋ Manual Send"
                                : "⏰ Scheduled"}
                            </span>{" "}
                            <span
                              className={`badge ${selectedLogDetail.mode === "ai" ? "bg-purple" : "bg-secondary"}`}
                              style={{
                                fontSize: "0.7rem",
                                background:
                                  selectedLogDetail.mode === "ai"
                                    ? "#7c3aed"
                                    : undefined,
                              }}
                            >
                              {selectedLogDetail.mode === "ai"
                                ? "🤖 AI Generated"
                                : "📝 HTML"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Category & Date Card */}
                    <div className="col-md-4">
                      <div className="d-flex align-items-center p-3 bg-white rounded-3 shadow-sm h-100">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{
                            width: "44px",
                            height: "44px",
                            background:
                              "linear-gradient(135deg, #4facfe, #00f2fe)",
                            flexShrink: 0,
                          }}
                        >
                          <Clock size={20} color="white" />
                        </div>
                        <div>
                          <div
                            className="text-muted"
                            style={{
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                            }}
                          >
                            Sent At
                          </div>
                          <div
                            className="fw-semibold"
                            style={{ fontSize: "0.9rem" }}
                          >
                            {formatDate(selectedLogDetail.date)}
                          </div>
                          {selectedLogDetail.category && (
                            <span
                              className={`badge ${selectedLogDetail.category === "forSubscriber" ? "bg-info" : selectedLogDetail.category === "forRegister" ? "bg-success" : "bg-secondary"}`}
                              style={{ fontSize: "0.7rem" }}
                            >
                              {selectedLogDetail.category === "forSubscriber"
                                ? "Subscriber"
                                : selectedLogDetail.category === "forRegister"
                                  ? "Register"
                                  : selectedLogDetail.category}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subject Line */}
                <div
                  className="px-4 py-3"
                  style={{ borderBottom: "1px solid #eee" }}
                >
                  <div
                    className="text-muted"
                    style={{
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "4px",
                    }}
                  >
                    Subject
                  </div>
                  <div className="fw-bold" style={{ fontSize: "1.1rem" }}>
                    {selectedLogDetail.subject || "(No subject saved)"}
                  </div>
                </div>

                {/* Email Body */}
                <div className="p-4">
                  <div
                    className="text-muted mb-2"
                    style={{
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Email Body
                  </div>
                  {selectedLogDetail.body ? (
                    <div
                      style={{
                        background: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: "12px",
                        padding: "24px",
                        maxHeight: "500px",
                        overflowY: "auto",
                        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.04)",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: selectedLogDetail.body,
                      }}
                    />
                  ) : (
                    <div className="text-center py-5">
                      <MailOpen size={40} className="text-muted mb-2" />
                      <p className="text-muted">
                        Email body not available for this log entry.
                        <br />
                        <small>
                          Older logs may not have saved the full email content.
                        </small>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="modal-footer border-0"
                style={{ padding: "16px 30px" }}
              >
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setSelectedLogDetail(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit User Modal */}
      {showUserModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingId ? "Edit User" : "Add New User"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelEdit}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        User Name
                      </label>
                      <input
                        type="text"
                        name="userName"
                        value={formData.userName}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter user name"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        User Email
                      </label>
                      <input
                        type="email"
                        name="UserEmail"
                        value={formData.UserEmail}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="Enter email"
                        required
                      />
                    </div>
                    <div className="col-md-12">
                      <label className="form-label fw-semibold">
                        Email Category
                      </label>
                      <div className="d-flex gap-2">
                        <select
                          name="category"
                          value={
                            isAddingNewCategory ? "addNew" : formData.category
                          }
                          onChange={(e) => {
                            if (e.target.value === "addNew") {
                              setIsAddingNewCategory(true);
                            } else {
                              setIsAddingNewCategory(false);
                              handleChange(e);
                            }
                          }}
                          className="form-select"
                          required
                        >
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat === "forSubscriber"
                                ? "For Subscriber"
                                : cat === "forRegister"
                                  ? "For Register"
                                  : cat}
                            </option>
                          ))}
                          <option value="addNew">+ Add New Category...</option>
                        </select>
                        {isAddingNewCategory && (
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter new category"
                              value={newCategory}
                              onChange={(e) => {
                                setNewCategory(e.target.value);
                                setFormData((prev) => ({
                                  ...prev,
                                  category: e.target.value,
                                }));
                              }}
                              required
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setIsAddingNewCategory(false);
                                setFormData((prev) => ({
                                  ...prev,
                                  category: "forSubscriber",
                                }));
                              }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-12 mt-4">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <label className="form-label fw-semibold mb-0">
                          Custom Fields
                        </label>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleAddField}
                        >
                          <Plus size={14} className="me-1" /> Add Field
                        </button>
                      </div>

                      {formData.customFields.map((field, index) => (
                        <div
                          key={index}
                          className="row g-2 mb-2 align-items-end p-2 bg-light rounded border"
                        >
                          <div className="col-md-4">
                            <label className="form-label small mb-1">
                              Label
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Company Name"
                              value={field.label}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "label",
                                  e.target.value,
                                )
                              }
                              required
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label small mb-1">
                              Value
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. Acme Corp"
                              value={field.value}
                              onChange={(e) =>
                                handleFieldChange(
                                  index,
                                  "value",
                                  e.target.value,
                                )
                              }
                              required
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small mb-1">
                              Shortcode
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm bg-white"
                              value={`{{${field.shortcode}}}`}
                              readOnly
                            />
                          </div>
                          <div className="col-md-1 text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveField(index)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                      {formData.customFields.length === 0 && (
                        <p className="text-muted small text-center my-3 italic">
                          No custom fields added yet.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={cancelEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : editingId
                        ? "Update User"
                        : "Add User"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {showImportModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div
              className="modal-content border-0 shadow-lg"
              style={{ borderRadius: "16px" }}
            >
              <div
                className="modal-header border-0 pb-0"
                style={{ padding: "30px 30px 10px" }}
              >
                <h5 className="modal-title d-flex align-items-center fw-bold text-primary">
                  <Upload className="me-2" size={24} />
                  Import Contacts
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                <ul
                  className="nav nav-pills mb-4 bg-light p-1 rounded-pill"
                  style={{ width: "fit-content" }}
                >
                  <li className="nav-item">
                    <button
                      className={`nav-link rounded-pill px-4 ${importTab === "csv" ? "active shadow-sm" : "text-muted"}`}
                      onClick={() => setImportTab("csv")}
                    >
                      <FileText size={18} className="me-2" />
                      CSV File
                    </button>
                  </li>
                  <li className="nav-item">
                    <button
                      className={`nav-link rounded-pill px-4 ${importTab === "sheets" ? "active shadow-sm" : "text-muted"}`}
                      onClick={() => setImportTab("sheets")}
                    >
                      <Download size={18} className="me-2" />
                      Google Sheets
                    </button>
                  </li>
                </ul>

                {importTab === "csv" ? (
                  <div className="import-zone border-dashed rounded-4 p-5 text-center bg-light-subtle">
                    <Upload
                      size={48}
                      className="text-primary mb-3 opacity-50"
                    />
                    <h6 className="fw-bold">Upload your CSV file</h6>
                    <p className="text-muted small mb-4">
                      Make sure your CSV has <b>Email</b>, <b>Name</b>, and{" "}
                      <b>Category</b> columns.
                    </p>
                    <input
                      type="file"
                      accept=".csv"
                      className="d-none"
                      id="csvUpload"
                      onChange={handleCsvFileChange}
                      disabled={importLoading}
                    />
                    <label
                      htmlFor="csvUpload"
                      className="btn btn-primary px-4 py-2 rounded-pill shadow-sm"
                    >
                      {importLoading ? (
                        <Loader2
                          className="spinner-border-sm me-2 animate-spin"
                          size={18}
                        />
                      ) : null}
                      Choose File
                    </label>
                    <div className="mt-4 pt-4 border-top">
                      <p className="small text-muted mb-2">Need a template?</p>
                      <button
                        onClick={downloadSampleCSV}
                        className="btn btn-link btn-sm text-primary text-decoration-none d-inline-flex align-items-center"
                      >
                        <Download size={14} className="me-1" />
                        Download Sample CSV
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="sheets-zone">
                    <div className="mb-4">
                      <label className="form-label fw-bold small text-uppercase">
                        Google Sheets Public Link
                      </label>
                      <div className="input-group shadow-sm rounded-4 overflow-hidden">
                        <span className="input-group-text bg-white border-end-0">
                          <Link size={18} className="text-muted" />
                        </span>
                        <input
                          type="text"
                          className="form-control border-start-0 py-3"
                          placeholder="https://docs.google.com/spreadsheets/d/..."
                          value={googleSheetLink}
                          onChange={(e) => setGoogleSheetLink(e.target.value)}
                          disabled={importLoading}
                        />
                        <button
                          className="btn btn-primary px-4"
                          onClick={handleGoogleSheetsImport}
                          disabled={importLoading}
                        >
                          {importLoading ? "Fetching..." : "Import"}
                        </button>
                      </div>
                    </div>

                    <div
                      className="alert alert-info border-0 rounded-4 d-flex align-items-start shadow-sm"
                      style={{ backgroundColor: "#eef2ff", color: "#4338ca" }}
                    >
                      <AlertCircle className="me-3 mt-1" size={24} />
                      <div>
                        <h6 className="fw-bold mb-1">
                          How to import from Google Sheets?
                        </h6>
                        <ol className="small mb-0 ps-3">
                          <li>
                            Click <b>Share</b> in your Google Sheet
                          </li>
                          <li>
                            Change access to <b>"Anyone with the link"</b>
                          </li>
                          <li>Copy and paste the link above</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-3 bg-light rounded-4 border border-light-subtle">
                  <h6 className="fw-bold small text-uppercase mb-3 d-flex align-items-center">
                    <CheckCircle2 size={16} className="me-2 text-success" />
                    Data Format Guide
                  </h6>
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div className="p-2 bg-white rounded border text-center">
                        <code className="text-primary fw-bold">UserEmail</code>
                        <div className="small text-muted mt-1">Required</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-2 bg-white rounded border text-center">
                        <code className="text-dark">userName</code>
                        <div className="small text-muted mt-1">Optional</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="p-2 bg-white rounded border text-center">
                        <code className="text-dark">category</code>
                        <div className="small text-muted mt-1">Optional</div>
                      </div>
                    </div>
                  </div>
                  <p className="small text-muted mt-3 mb-0">
                    * Any additional columns (e.g. "Phone", "Company") will be
                    automatically added as <b>Custom Fields</b>.
                  </p>
                </div>
              </div>
              <div className="modal-footer border-0 p-4 pt-0">
                <button
                  className="btn btn-link text-muted text-decoration-none"
                  onClick={() => setShowImportModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserEmailTemplateManager;

// Add modern premium dark styles for the manager component
const style = document.createElement("style");
style.textContent = `
  /* Global Component Scoped Styles */
  #premium-dark-manager {
    color: #f3f4f6;
  }
  
  /* Cards */
  #premium-dark-manager .card {
    background: #111827 !important;
    border: 1px solid rgba(255, 255, 255, 0.08) !important;
    border-radius: 1.25rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    overflow: hidden;
  }
  #premium-dark-manager .card-header {
    background: linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    color: #fff !important;
  }

  /* Modals */
  #premium-dark-manager .modal-content {
    background: #111827 !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 1.25rem !important;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.2) !important;
    color: #f3f4f6 !important;
  }
  #premium-dark-manager .modal-header {
    background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%) !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    color: #fff !important;
  }
  #premium-dark-manager .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.05) !important;
    background: rgba(255, 255, 255, 0.01) !important;
  }
  #premium-dark-manager .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%);
  }

  /* Table */
  #premium-dark-manager .table, 
  #premium-dark-manager .table td, 
  #premium-dark-manager .table th,
  #premium-dark-manager .table-responsive {
    color: #f3f4f6 !important;
    border-color: rgba(255, 255, 255, 0.05) !important;
  }
  #premium-dark-manager .table-light,
  #premium-dark-manager .table-light th {
    background-color: rgba(255, 255, 255, 0.03) !important;
    color: #9ca3af !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
  }
  #premium-dark-manager .table-hover tbody tr:hover td {
    background-color: rgba(255, 255, 255, 0.03) !important;
    color: #fff !important;
  }
  #premium-dark-manager .table td {
    vertical-align: middle;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
    background: transparent !important;
    padding: 1rem 0.75rem !important; /* Fix for table height */
  }

  /* Pagination */
  #premium-dark-manager .pagination .page-link {
    background-color: #1f2937 !important;
    border-color: rgba(255, 255, 255, 0.05) !important;
    color: #9ca3af !important;
    transition: all 0.2s;
  }
  #premium-dark-manager .pagination .page-link:hover {
    background-color: rgba(255, 255, 255, 0.1) !important;
    color: #fff !important;
  }
  #premium-dark-manager .pagination .page-item.active .page-link {
    background-color: #0ea5e9 !important;
    border-color: #0ea5e9 !important;
    color: #ffffff !important;
  }
  #premium-dark-manager .pagination .page-item.disabled .page-link {
    background-color: #111827 !important;
    color: #4b5563 !important;
    border-color: rgba(255, 255, 255, 0.02) !important;
  }

  /* Inputs & Forms */
  #premium-dark-manager .form-control,
  #premium-dark-manager .form-select {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    color: #f3f4f6 !important;
    border-radius: 0.5rem;
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;
  }
  #premium-dark-manager .form-control:focus,
  #premium-dark-manager .form-select:focus {
    background-color: rgba(0, 0, 0, 0.3) !important;
    border-color: rgba(56, 189, 248, 0.5) !important;
    box-shadow: 0 0 0 4px rgba(56, 189, 248, 0.1) !important;
  }
  #premium-dark-manager .form-label {
    color: #9ca3af !important;
    font-size: 0.85rem;
    font-weight: 600;
    letter-spacing: 0.025em;
  }
  #premium-dark-manager .input-group-text {
    background-color: rgba(0, 0, 0, 0.2) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    color: #9ca3af !important;
  }

  /* Dropzone & Import Styling */
  #premium-dark-manager .border-dashed {
    border: 2px dashed rgba(255,255,255,0.2) !important;
    transition: all 0.3s ease;
    background-color: rgba(255,255,255,0.02) !important;
  }
  #premium-dark-manager .border-dashed:hover {
    border-color: #38bdf8 !important;
    background-color: rgba(56,189,248,0.05) !important;
  }
  #premium-dark-manager .bg-light-subtle {
    background-color: rgba(255,255,255,0.02) !important;
  }
  #premium-dark-manager .nav-pills .nav-link {
    color: #9ca3af !important;
    border-radius: 0.5rem;
  }
  #premium-dark-manager .nav-pills .nav-link:hover {
    background-color: rgba(255,255,255,0.05);
  }
  #premium-dark-manager .nav-pills .nav-link.active {
    background-color: #0ea5e9 !important;
    color: #fff !important;
    box-shadow: 0 4px 6px -1px rgba(14, 165, 233, 0.3);
  }

  /* Buttons */
  #premium-dark-manager .btn-primary {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
    border: none !important;
    color: white !important;
    box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2);
  }
  #premium-dark-manager .btn-outline-primary {
    color: #38bdf8 !important;
    border-color: rgba(56,189,248,0.3) !important;
    background: rgba(56,189,248,0.05) !important;
  }
  #premium-dark-manager .btn-outline-primary:hover {
    background: rgba(56,189,248,0.15) !important;
    color: #fff !important;
  }
  #premium-dark-manager .btn-success {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
    border: none !important;
  }
  
  /* Danger and Warning buttons in table */
  #premium-dark-manager .btn-warning {
    background: rgba(245, 158, 11, 0.15) !important;
    color: #fbbf24 !important;
    border: 1px solid rgba(245, 158, 11, 0.3) !important;
  }
  #premium-dark-manager .btn-warning:hover {
    background: rgba(245, 158, 11, 0.25) !important;
    color: #fcd34d !important;
  }
  #premium-dark-manager .btn-danger {
    background: rgba(239, 68, 68, 0.15) !important;
    color: #f87171 !important;
    border: 1px solid rgba(239, 68, 68, 0.3) !important;
  }
  #premium-dark-manager .btn-danger:hover {
    background: rgba(239, 68, 68, 0.25) !important;
    color: #fca5a5 !important;
  }

  /* Badges & Text */
  #premium-dark-manager .text-muted {
    color: #9ca3af !important;
  }
  #premium-dark-manager .bg-white {
    background: #1f2937 !important;
  }
  #premium-dark-manager .text-dark {
    color: #f3f4f6 !important;
  }
  #premium-dark-manager .badge {
    padding: 0.4em 0.8em;
    font-weight: 600;
    letter-spacing: 0.025em;
  }
  #premium-dark-manager .bg-info {
    background-color: rgba(14, 165, 233, 0.2) !important;
    color: #38bdf8 !important;
    border: 1px solid rgba(14, 165, 233, 0.3) !important;
  }
  #premium-dark-manager .bg-success {
    background-color: rgba(16, 185, 129, 0.2) !important;
    color: #34d399 !important;
    border: 1px solid rgba(16, 185, 129, 0.3) !important;
  }
  #premium-dark-manager .bg-secondary {
    background-color: rgba(156, 163, 175, 0.15) !important;
    color: #d1d5db !important;
    border: 1px solid rgba(156, 163, 175, 0.3) !important;
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .import-zone {
    cursor: pointer;
  }
`;
document.head.appendChild(style);
